import type { ECElementEvent, EChartsType } from 'echarts/core';
import {
  IEChartsModelAccess,
  IFindingSpan,
  IFindingSpanSeries,
  IFindingSpanStep,
  TGraphPoint,
} from './graph-finding-spans.interface';
import {
  buildFindingSpanSeries,
  findFindingSpanIndexAt,
  FINDING_SPAN_LABELS_SERIES_ID,
  formatFindingLabelNumbers,
  getVisibleLabelAnchor,
  groupFindingLabels,
} from './graph-finding-spans.util';
import { getNextDotIndex } from './graph-pair-mode.util';

// Span-mode state shared by the line and timeline graphs (selection, clicks, next arrow, number labels,
// tooltip prefix).
// Composed into the graph component; series order is [spans line, middle dots, labels] from `seriesIndex`.
export class FindingSpansChart {
  private spans: IFindingSpan[] = [];
  private segments: TGraphPoint[][] = [];
  private color = '';
  private startDataIndexes: number[] = [];
  private seriesIndex = 0;
  private selectedIndex: number | null = null;
  private appliedLabelsKey = '';

  // Called on every option rebuild. ngx-echarts applies a new option with notMerge, which empties the labels
  // series again, so the next render regroups them.
  public buildSeries(
    spans: IFindingSpan[],
    segments: TGraphPoint[][],
    color: string,
    name: string,
    seriesIndex: number,
  ): IFindingSpanSeries['series'] {
    const { series, startDataIndexes } = buildFindingSpanSeries(spans, segments, color, name);

    this.spans = spans;
    this.segments = segments;
    this.color = color;
    this.startDataIndexes = startDataIndexes;
    this.seriesIndex = seriesIndex;
    this.selectedIndex = null;
    this.appliedLabelsKey = '';

    return series;
  }

  // The clicked finding's id; null (and no selection) when the click hit something else.
  public select(chart: EChartsType | null, event: ECElementEvent): string | null {
    const index = this.getClickedIndex(chart, event);

    this.selectedIndex = index >= 0 ? index : null;

    return index >= 0 ? this.spans[index].id : null;
  }

  public clearSelection(): void {
    this.selectedIndex = null;
  }

  public selectNext(): IFindingSpanStep | null {
    if (!this.spans.length) {
      return null;
    }

    this.selectedIndex = getNextDotIndex(this.selectedIndex, this.spans.length);

    return {
      dataIndex: this.startDataIndexes[this.selectedIndex],
      seriesIndex: this.seriesIndex,
      span: this.spans[this.selectedIndex],
    };
  }

  // "3 · " (or "4, 5 · ") in front of the tooltip while the pointer is on findings. The axis pointer snaps to
  // the nearest data point, span ends included, so a pointer on a short finding still lands inside it.
  // axisValue is that snapped x, where the pointer line is drawn and whose time the tooltip shows, so the
  // number always matches the tooltip; the raw pointer x would miss zero-length findings.
  public prefixTooltip(axisValue: unknown, text: string): string {
    if (typeof axisValue !== 'number') {
      return text;
    }

    const numbers = this.spans
      .filter((span) => span.start <= axisValue && axisValue <= span.end)
      .map((span) => span.number);

    if (!numbers.length) {
      return text;
    }

    return `<b style="color: ${this.color}">${formatFindingLabelNumbers(numbers)}</b> · ${text}`;
  }

  // Runs after every render (zoom, pan, resize, new option); only touches the chart when the labels changed.
  // Applied in a microtask: the first render happens inside ECharts' main process, which rejects setOption.
  // Graphs without spans return right away (a rebuild to no spans already empties the labels series).
  public updateLabels(chart: EChartsType): void {
    if (!this.spans.length) {
      return;
    }

    const visibleRange = this.getVisibleRange(chart);
    const labels = groupFindingLabels(
      this.spans.flatMap((span, index) => {
        const anchor = getVisibleLabelAnchor(span, this.segments[index], visibleRange);

        return anchor
          ? [{ ...anchor, pixelX: chart.convertToPixel({ xAxisIndex: 0 }, anchor.value[0]) as number }]
          : [];
      }),
    );
    const labelsKey = labels.map(({ id, text, value }) => `${id}:${text}:${value[0]}:${value[1]}`).join('|');

    if (labelsKey === this.appliedLabelsKey) {
      return;
    }

    this.appliedLabelsKey = labelsKey;
    queueMicrotask(() => {
      if (chart.isDisposed()) {
        return;
      }

      chart.setOption({
        series: [
          {
            data: labels.map((label) => ({ label: { formatter: label.text }, name: label.id, value: label.value })),
            id: FINDING_SPAN_LABELS_SERIES_ID,
          },
        ],
      });
    });
  }

  // The x range on screen after zooming (the axis extent follows dataZoom). ECharts has no public getter:
  // `getModel()` is public at runtime but typed private, so it is reached through a cast.
  private getVisibleRange(chart: EChartsType): [number, number] {
    const xAxisModel = (chart as unknown as IEChartsModelAccess).getModel().getComponent('xAxis', 0);

    return xAxisModel?.axis?.scale.getExtent() ?? [-Infinity, Infinity];
  }

  private getClickedIndex(chart: EChartsType | null, event: ECElementEvent): number {
    if (event.seriesIndex === this.seriesIndex) {
      const offsetX = event.event?.offsetX;

      if (!chart || typeof offsetX !== 'number') {
        return -1;
      }

      return findFindingSpanIndexAt(this.spans, chart.convertFromPixel({ xAxisIndex: 0 }, offsetX) as number);
    }

    if (event.seriesIndex === this.seriesIndex + 1 || event.seriesIndex === this.seriesIndex + 2) {
      return this.spans.findIndex((span) => span.id === event.name);
    }

    return -1;
  }
}
